import {IConfigData, ICreateProjectResult, IEditorUtil, SF} from './interfaces';
import { CustomizerEditor } from './customizer-editor';
import { CustomizerJsonApi, IThemeData } from './customizer-json-api';
import {EditorCache} from "@/editor/base/cache-base";

function doTranslations(element: HTMLElement|Document, result: ICreateProjectResult) {
    element.querySelectorAll<HTMLElement>('[data-translate]').forEach(element => {
        const translation = element.getAttribute('data-translate');
        const defaultTranslation = element.getAttribute('data-translate-default') || translation;
        const translateOptions = element.getAttribute('data-translate-options');
        let replacements: { [key: string]: string } = {};
        if (translateOptions) {
            try {
                const replacementParse = JSON.parse(translateOptions);
                Object.keys(replacementParse).forEach(key => {
                    replacements[key] = (result as any)[replacementParse[key]] as string;
                });
            } catch (e) {
                console.error('Error parsing translate options', e);
            }
        }
        const translationText = SF.tr(translation, replacements, defaultTranslation, false);
        if (element.getAttribute('data-translate-raw') === 'true') {
            element.innerHTML = translationText;
        } else {
            element.innerText = translationText;
        }
    });
}

function updateBrowserHistory(partnerSku: string|null, projectGuid: string|null, variantSku: string|null, quantity: string,
      projectId: string, shopUrl: string, sessionId: string, messageEnvironment: string, previewKey: string,
      appProxyAuthUrl: string, personalizerPassThroughUrl: string, tokenTransport: string,
      appProxyParentOrigin: string) {
    const newParams: { [key: string]: string } = {
        quantity: quantity,
        photo_project_id: projectId,
        shop_url: shopUrl,
        session_identifier: sessionId,
        message_environment: messageEnvironment,
        preview_key: previewKey,
        personalizer_passthrough_url: personalizerPassThroughUrl
    };
    if (appProxyAuthUrl) {
        newParams.app_proxy_auth_url = appProxyAuthUrl;
    }
    if (tokenTransport === 'parent') {
        newParams.app_proxy_token_transport = tokenTransport;
        newParams.app_proxy_parent_origin = appProxyParentOrigin;
    }
    if (partnerSku) {
        newParams.partner_sku = partnerSku;
    }
    if (projectGuid && variantSku) {
        newParams.project_guid = projectGuid;
        newParams.variant_sku = variantSku;
    }
    const replaceParams = new URLSearchParams(newParams);
    history.replaceState(null, '', location.pathname + '?' + replaceParams);
}

function updateText(result: ICreateProjectResult) {
    if (result.editorSubType === 'front_back_template') {
        document.querySelector<HTMLDivElement>('#front-back-tabs').style.display = 'flex';
    }

    if (result.pageNames.length > 1) {
        const pageTabs = document.querySelector<HTMLDivElement>('#page-tabs');
        pageTabs.style.display = 'flex';
        let innerHtml = '<ul>';
        result.pageNames.forEach((pageName, index) => {
            innerHtml += `<li data-page="${index}"  ${index === 0 ? ' class="is-active"' : ''}>
                    <a>
                        <span>${pageName}</span>
                    </a>
                </li>`;
        });
        pageTabs.innerHTML = innerHtml + '</ul>';
    }

    document.querySelector<HTMLDivElement>('#template-name')!.innerText = result.templateName;
    document.querySelector<HTMLDivElement>('#product-name')!.innerText = result.productName;

    if (result.returnPolicyUrl) {
        document.querySelector<HTMLAnchorElement>('#terms-and-conditions-section-3')!.style.display = 'block';
    }
}

function updateTheme(theme: IThemeData):void {
    const styleSheets = document.styleSheets;

    for (let i = 0; i < styleSheets.length; i++) {
        // noinspection JSDeprecatedSymbols
        const rules = styleSheets[i].cssRules || styleSheets[i].rules;

        for (let j = 0; j < rules.length; j++) {
            const rule:CSSStyleRule = rules[j] as CSSStyleRule;

            theme.svg.forEach(svgRule => {
                if (rule.selectorText && rule.selectorText.includes(svgRule.selector) && rule.style.backgroundImage) {
                    if (svgRule.value) {
                        const dynamicRegex = new RegExp(`fill:%23[0-9a-fA-F]{6}/\\*@@${svgRule.replace}@@\\*/`);

                        rule.style.backgroundImage = rule.style.backgroundImage.replace(dynamicRegex,
                            `fill:${svgRule.value.replace('#', '%23')}/*@@${svgRule.replace}@@*/`);
                    } else {
                        console.log(`ThemeElement ${svgRule.replace} has no value`);
                    }
                }
            });
        }
    }

    Object.keys(theme.root).forEach(key => {
        document.documentElement.style.setProperty(`--${key}`, theme.root[key]);
    });
}

function showError(errorMessageText: string) {
    const errorMessage = document.querySelector<HTMLDivElement>('#error-message');
    errorMessage.style.display = 'block';
    errorMessage.innerText = errorMessageText;
    document.querySelector<HTMLDivElement>('#please-wait #please-wait-message')!.style.display = 'none';
}

function getValidatedServiceUrl(value: string|null): string|null {
    if (!value) {
        return null;
    }

    try {
        const url = new URL(value);
        const isLoopback = url.hostname === 'localhost' || url.hostname === '127.0.0.1' || url.hostname === '[::1]';
        const isLocalNetworkUrl = isLoopback || url.hostname.endsWith('.local');
        const frontendIsLocal = location.hostname === 'localhost' || location.hostname === '127.0.0.1' ||
            location.hostname === '[::1]' || location.hostname.endsWith('.local');
        if ((url.protocol !== 'https:' && !(isLoopback && url.protocol === 'http:')) ||
            url.username || url.password || (isLocalNetworkUrl && !frontendIsLocal)) {
            return null;
        }
        return value;
    } catch {
        return null;
    }
}

function getValidatedParentOrigin(value: string|null): string|null {
    if (!value) {
        return null;
    }

    try {
        const url = new URL(value);
        const isLoopback = url.hostname === 'localhost' || url.hostname === '127.0.0.1' || url.hostname === '[::1]';
        const frontendIsLocal = location.hostname === 'localhost' || location.hostname === '127.0.0.1' ||
            location.hostname === '[::1]' || location.hostname.endsWith('.local');
        if ((url.protocol !== 'https:' && !(isLoopback && frontendIsLocal && url.protocol === 'http:')) ||
            url.username || url.password || url.pathname !== '/' || url.search || url.hash) {
            return null;
        }
        return url.origin;
    } catch {
        return null;
    }
}

function getConfigData():Promise<IConfigData> {
    return new Promise<IConfigData>((resolve, reject) => {
        try {
            fetch('/runtime-settings.json').then(response => {
                if (!response.ok) {
                    reject(`HTTP error! Status: ${response.status}`);
                }
                resolve(response.json());
            });
        } catch (error) {
            reject(error);
        }
    });

}

 function initEditor() {
    SF.Util = SF.Util || {};
     (SF.Util as IEditorUtil).Cache = new EditorCache();
    // continue editor startup...
}


doTranslations(document.querySelector<HTMLElement>('#please-wait'), {} as any);

const configDataPromise = getConfigData()

SF.onReady(async () => {
    initEditor();
    const searchParams = new URLSearchParams(location.search);
    let result: ICreateProjectResult;
    const tokenTransport = searchParams.get('app_proxy_token_transport') === 'parent' ? 'parent' : 'direct';
    if ((searchParams.has('partner_sku') || (searchParams.has('project_guid') && searchParams.has('variant_sku'))) &&
        searchParams.has('session_identifier') && searchParams.has('shop_url') &&
        (tokenTransport === 'parent' || searchParams.has('app_proxy_auth_url')) &&
        searchParams.has('personalizer_passthrough_url')) {
        const partnerSku = searchParams.get('partner_sku');
        const projectGuid = searchParams.get('project_guid');
        const variantSku = searchParams.get('variant_sku');
        const quantity = searchParams.get('quantity') || '1';
        const shopUrl = searchParams.get('shop_url');
        const sessionId = searchParams.get('session_identifier');
        const previewKey = searchParams.get('preview_key') || null;
        const appProxyAuthUrl = getValidatedServiceUrl(searchParams.get('app_proxy_auth_url'));
        const appProxyParentOrigin = tokenTransport === 'parent'
            ? getValidatedParentOrigin(searchParams.get('app_proxy_parent_origin'))
            : null;
        const personalizerPassThroughUrl = getValidatedServiceUrl(searchParams.get('personalizer_passthrough_url'));
        if (!personalizerPassThroughUrl || (tokenTransport === 'parent' ? !appProxyParentOrigin : !appProxyAuthUrl)) {
            showError('Missing or invalid service URL');
            return;
        }
        let configData ;
        try {
            configData = await configDataPromise;
        } catch (err) {
            console.error('Error loading config data:', err);
        }
        window.SF.staticUrlRoot = configData.static_url_root;
        window.SF.currentLanguage = configData.default_language;
        window.SF.personalizerPassThrough = personalizerPassThroughUrl;
        window.SF.showTranslate = false;
        window.SF.ghostOpacity = configData.ghost_opacity;
        window.SF.maxImageSize = configData.max_image_size;

        const messageEnvironment = searchParams.get('message_environment') || configData.message_environment;

        const jsonApi = new CustomizerJsonApi(shopUrl, sessionId, messageEnvironment, appProxyAuthUrl,
            tokenTransport, appProxyParentOrigin);

        const themePromise = new Promise<void>((resolve, reject:(error:string) => void) => {
            jsonApi.getThemeData().then(theme => {
                updateTheme(theme);
                resolve();
            }, err => {
                reject(err)
            });
        });

        // After we get rid of the Entity Manager Closed error, we can move this await until after the createProject call
        // so that they can be done in parallel.
        try {
            await themePromise;
        } catch(err) {
            showError(err);
            return;
        }

        const cartItemIdParam = searchParams.get('cart_item_id') || null;
        try {
            result = await jsonApi.createProject(partnerSku,
                projectGuid,
                variantSku,
                cartItemIdParam,
                searchParams.get('photo_project_id') || null,
                quantity
            );
        } catch (err) {
            showError(err);
            return;
        }

        updateBrowserHistory(partnerSku, projectGuid, variantSku, quantity, result.projectId.toString(),
            shopUrl, sessionId, messageEnvironment, previewKey, appProxyAuthUrl, personalizerPassThroughUrl,
            tokenTransport, appProxyParentOrigin);

        updateText(result);

        doTranslations(document, result);

        document.querySelector<HTMLDivElement>('#please-wait')!.style.display = 'none';
        document.querySelector<HTMLDivElement>('#main-container')!.style.display = 'block';

        // Fix pages for Perosonalizer.  customerEditable is what we care about, not canEdit.
        result.pageList.forEach(page => {
            page.edits.freeObject.forEach(freeObject => {
               if (typeof(freeObject.customerEditable) === 'boolean'){
                   freeObject.canEdit = freeObject.customerEditable;
               }
            });
        });

        const pagePreviews: string[] = [];
        if (Array.isArray(result.itePagePreviews)) {
            result.itePagePreviews.forEach(preview => {
               pagePreviews[preview.page] = preview.ite.replaceAll('\\\'', '\'');
            });
        }

        // noinspection JSUnusedLocalSymbols
        const customizerObj = new CustomizerEditor(
            jsonApi,
            sessionId,
            result.projectId,
            result.pageList,
            result.editorSubType === 'front_back_template',
            result.threeDModel,
            result.threeDColor || 'light',
            result.cartPreview,
            previewKey,
            result.maskPreviewData,
            result.threeDOverlay,
            result.nonPrintableBackgroundPreviewData,
            // The itePreview is getting escaped in the JSON, so we need to unescape it... This is currently being done
            // in CustomizerHandler ~ line 472.  For now just undo it, but investigate and remove it from there if it
            // is no longer necessary.  If you don't unescape it things will blow up.
            result.itePreview.replaceAll('\\\'', '\''),
            pagePreviews,
            result.iteUrl,
            result.warningErrorDetails,
            result.previewOutputFormat, // Maybe we look at webp in the future.
            result.productId,
            parseInt(quantity, 10),
            result.forceTextChange,
            result.showPreviewInForceModal,
            result.alwaysShowTermsAndConditions,
            result.previewOnSave,
            true,
            cartItemIdParam != null
        );
        // Order complete was added for debugging
        customizerObj.orderComplete = (complete:boolean, previewUrl: string, cartItemId: number) => {
            document.querySelector<HTMLDivElement>('#please-wait')!.style.display = 'flex';
            document.querySelector<HTMLDivElement>('#main-container')!.style.display = 'none';
            if (complete) {
                console.log('Order Complete for Cart Item ID: ' + cartItemId.toString());
            } else {
                console.log('Personalization Cancelled');
            }
        };

    } else {
        showError('Missing required parameters');
    }


});
